import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './ModuleRail.module.scss';
import type { ModuleId } from '../data/types';
import type { ModuleRailItem } from '../useAicaReportV2Logic';
import type { RailSubOption } from '../subOptionState';
import { DATA_NOT_AVAILABLE_LABEL, GO_TO_SECTION_LABEL, UNLOCK_MODULE_TOOLTIP } from '../constants';
import { useModuleRailTooltip } from './useModuleRailTooltip';
import { dataNotAvailable } from '../../../assets/hostedassets';
import Button from '../../../common/_custom/Button/Button';

const SearchIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

const CloseIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
);

const CheckTick: React.FC = () => (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DragHandleIcon: React.FC = () => (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        {[2, 8, 14].flatMap((y) => [
            <circle key={`l-${y}`} cx="2" cy={y} r="1.1" fill="currentColor" />,
            <circle key={`r-${y}`} cx="8" cy={y} r="1.1" fill="currentColor" />,
        ])}
    </svg>
);

// Lock icon — sourced from the Dashboard sidebar (`src/assets/grey-lock-2.svg`)
// so locked modules read with the same visual vocabulary as the global nav.
// Inlined (vs an <img> import) so the icon inherits `currentColor` and can be
// recoloured via the CSS class, matching the existing icon system here.
const LockIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
            d="M4.24967 16.0859C3.86009 16.0859 3.5267 15.9473 3.24951 15.6701C2.97231 15.393 2.83348 15.0593 2.83301 14.6693V7.58594C2.83301 7.19635 2.97184 6.86297 3.24951 6.58577C3.52717 6.30858 3.86056 6.16974 4.24967 6.16927H4.95801V4.7526C4.95801 3.77274 5.30344 2.93762 5.9943 2.24723C6.68516 1.55684 7.52029 1.21141 8.49967 1.21094C9.47953 1.21094 10.3149 1.55637 11.0058 2.24723C11.6966 2.93809 12.0418 3.77322 12.0413 4.7526V6.16927H12.7497C13.1393 6.16927 13.4729 6.3081 13.7505 6.58577C14.0282 6.86344 14.1668 7.19683 14.1663 7.58594V14.6693C14.1663 15.0589 14.0277 15.3925 13.7505 15.6701C13.4734 15.9478 13.1397 16.0864 12.7497 16.0859H4.24967ZM8.49967 12.5443C8.88926 12.5443 9.22288 12.4057 9.50055 12.1285C9.77822 11.8513 9.91681 11.5177 9.91634 11.1276C9.91634 10.738 9.77774 10.4046 9.50055 10.1274C9.22335 9.85024 8.88973 9.71141 8.49967 9.71094C8.11009 9.71094 7.7767 9.84977 7.49951 10.1274C7.22231 10.4051 7.08348 10.7385 7.08301 11.1276C7.08301 11.5172 7.22184 11.8508 7.49951 12.1285C7.77717 12.4061 8.11056 12.5447 8.49967 12.5443ZM6.37467 6.16927H10.6247V4.7526C10.6247 4.16233 10.4181 3.66059 10.0049 3.2474C9.59169 2.8342 9.08995 2.6276 8.49967 2.6276C7.9094 2.6276 7.40766 2.8342 6.99447 3.2474C6.58127 3.66059 6.37467 4.16233 6.37467 4.7526V6.16927Z"
            fill="currentColor"
        />
    </svg>
);

// "Data not available" indicator — sourced from the hosted product asset
// (`hostedassets.ts → dataNotAvailable`) so the icon stays in lockstep with
// the rest of the app's "no data" treatment. <img> instead of inline SVG
// because the asset is pre-coloured and not on currentColor.
const NoDataIcon: React.FC = () => <img src={dataNotAvailable} alt="" width={16} height={16} />;

export type ModuleRailProps = {
    items: ModuleRailItem[];
    searchTerm: string;
    setSearchTerm: (s: string) => void;
    toggleModule: (id: ModuleId) => void;
    toggleSubOption: (id: ModuleId, subId: string) => void;
    selectAll: () => void;
    deselectAll: () => void;
    allEnabled: boolean;
    reorder: (id: ModuleId, targetId: ModuleId, position?: 'before' | 'after') => void;
    loading?: boolean;
    onCardActivate?: (id: ModuleId) => void;
    onSubOptionActivate?: (id: ModuleId, subId: string) => void;
};

const SubOptionRow: React.FC<{
    option: RailSubOption;
    isChild?: boolean;
    onToggle: (subId: string) => void;
    onActivate: (subId: string) => void;
    onLabelEnter: (labelWrap: HTMLElement, fullLabel: string) => void;
    onLabelLeave: () => void;
}> = ({ option, isChild, onToggle, onActivate, onLabelEnter, onLabelLeave }) => (
    <div
        className={clsx(styles.SubRow, { [styles.Disabled]: !option.available, [styles.Child]: isChild })}
        onFocus={option.available ? (e) => onLabelEnter(e.currentTarget, option.label) : undefined}
        onBlur={onLabelLeave}
    >
        <span
            className={styles.SubLabelWrap}
            onMouseEnter={option.available ? (e) => onLabelEnter(e.currentTarget, option.label) : undefined}
            onMouseLeave={onLabelLeave}
        >
            <Button
                type="button"
                className={clsx(styles.SubLabel, styles.SubLabelButton)}
                text={
                    <>
                        <span className={styles.SubLabelName}>{option.labelParts?.name ?? option.label}</span>
                        {option.labelParts ? <span className={styles.SubLabelSuffix}> {option.labelParts.suffix}</span> : null}
                    </>
                }
                aria-label={`${GO_TO_SECTION_LABEL} ${option.label} in preview`}
                disabled={!option.on || !option.available}
                title={option.available ? undefined : DATA_NOT_AVAILABLE_LABEL}
                onClick={() => onActivate(option.id)}
            />
        </span>
        <Button
            type="button"
            className={clsx(styles.Toggle, { [styles.On]: option.on })}
            text={<span className={styles.Knob} />}
            aria-pressed={option.on}
            aria-label={option.label}
            disabled={!option.available}
            title={option.available ? undefined : DATA_NOT_AVAILABLE_LABEL}
            onClick={() => onToggle(option.id)}
        />
    </div>
);

const SubOptionPanel: React.FC<{
    options: RailSubOption[];
    onToggle: (subId: string) => void;
    onActivate: (subId: string) => void;
    onLabelEnter: (labelWrap: HTMLElement, fullLabel: string) => void;
    onLabelLeave: () => void;
}> = ({ options, onToggle, onActivate, onLabelEnter, onLabelLeave }) => (
    <div className={styles.SubPanel}>
        {options
            .filter((option) => !option.parentId)
            .map((option) => {
                const children = options.filter((child) => child.parentId === option.id);
                const row = (
                    <SubOptionRow
                        option={option}
                        onToggle={onToggle}
                        onActivate={onActivate}
                        onLabelEnter={onLabelEnter}
                        onLabelLeave={onLabelLeave}
                    />
                );
                if (!children.length) return <React.Fragment key={option.id}>{row}</React.Fragment>;
                return (
                    <div key={option.id} className={styles.SubParent}>
                        {row}
                        {option.on ? (
                            <div className={styles.SubGroup}>
                                <div className={styles.Category}>{option.childrenHeading}</div>
                                <div className={styles.SubGroupList} role="group" aria-label={option.childrenHeading}>
                                    {children.map((child) => (
                                        <SubOptionRow
                                            key={child.id}
                                            option={child}
                                            isChild
                                            onToggle={onToggle}
                                            onActivate={onActivate}
                                            onLabelEnter={onLabelEnter}
                                            onLabelLeave={onLabelLeave}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
    </div>
);

const SkeletonCards: React.FC<{ count?: number }> = ({ count = 8 }) => (
    <>
        {Array.from({ length: count }).map((_, i) => (
            <div
                key={i}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 12,
                    border: '1px solid #EDECE6',
                    borderRadius: 8,
                    background: '#FFFFFF',
                    marginBottom: 8,
                    opacity: 1 - i * 0.06,
                }}
            >
                <div
                    style={{
                        width: 10,
                        height: 16,
                        background: '#F0EFE9',
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                />
                <div style={{ flex: 1 }}>
                    <div
                        style={{
                            height: 8,
                            width: '40%',
                            background: '#F5F4EE',
                            borderRadius: 4,
                            marginBottom: 6,
                            animation: 'aicaShimmer 1.4s ease-in-out infinite',
                        }}
                    />
                    <div
                        style={{
                            height: 10,
                            width: '70%',
                            background: '#EDECE6',
                            borderRadius: 4,
                            animation: 'aicaShimmer 1.4s ease-in-out infinite',
                            animationDelay: '0.2s',
                        }}
                    />
                </div>
                <div
                    style={{
                        width: 32,
                        height: 18,
                        background: '#F0EFE9',
                        borderRadius: 9,
                        flexShrink: 0,
                    }}
                />
            </div>
        ))}
        <style>{`
            @keyframes aicaShimmer {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.55; }
            }
        `}</style>
    </>
);

type DropMark = { id: ModuleId; position: 'before' | 'after' } | null;

export const ModuleRail: React.FC<ModuleRailProps> = ({
    items,
    searchTerm,
    setSearchTerm,
    toggleModule,
    toggleSubOption,
    onCardActivate,
    onSubOptionActivate,
    selectAll,
    deselectAll,
    allEnabled,
    reorder,
    loading = false,
}) => {
    const [dragId, setDragId] = useState<ModuleId | null>(null);
    const [dropMark, setDropMark] = useState<DropMark>(null);
    const lastMarkRef = useRef<string>('');
    const { tooltip, openTooltip, closeTooltip, openTruncatedLabelTooltip } = useModuleRailTooltip(styles.SubRow, styles.SubLabelName);
    const transparentGifRef = useRef<HTMLImageElement | null>(null);

    // Tiny transparent PNG used to suppress the browser's ghost image (cleaner FLIP-style feel).
    const getTransparentImage = (): HTMLImageElement => {
        if (transparentGifRef.current) return transparentGifRef.current;
        const img = new Image();
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        transparentGifRef.current = img;
        return img;
    };

    const onDragStart = (id: ModuleId, locked: boolean) => (e: React.DragEvent) => {
        if (locked) {
            e.preventDefault();
            return;
        }
        setDragId(id);
        try {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id);
            e.dataTransfer.setDragImage(getTransparentImage(), 0, 0);
        } catch {
            /* noop */
        }
    };

    const dragOverRafRef = useRef<number | null>(null);
    const pendingMarkRef = useRef<{ id: ModuleId; clientY: number; rect: DOMRect } | null>(null);

    const onDragOver = (id: ModuleId) => (e: React.DragEvent<HTMLDivElement>) => {
        if (!dragId || dragId === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Read layout synchronously (cheap because the row is already laid out) but defer the
        // setState to the next animation frame so rapid dragover events coalesce into one render.
        pendingMarkRef.current = {
            id,
            clientY: e.clientY,
            rect: (e.currentTarget as HTMLDivElement).getBoundingClientRect(),
        };
        if (dragOverRafRef.current != null) return;
        dragOverRafRef.current = requestAnimationFrame(() => {
            dragOverRafRef.current = null;
            const pending = pendingMarkRef.current;
            pendingMarkRef.current = null;
            if (!pending) return;
            const midY = pending.rect.top + pending.rect.height / 2;
            const position: 'before' | 'after' = pending.clientY < midY ? 'before' : 'after';
            const key = `${pending.id}:${position}`;
            if (lastMarkRef.current === key) return;
            lastMarkRef.current = key;
            setDropMark({ id: pending.id, position });
        });
    };

    const flushDragOverRaf = () => {
        if (dragOverRafRef.current != null) {
            cancelAnimationFrame(dragOverRafRef.current);
            dragOverRafRef.current = null;
        }
        pendingMarkRef.current = null;
    };

    const onDrop = (id: ModuleId) => (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        flushDragOverRaf();
        if (dragId && dropMark && dragId !== id) {
            reorder(dragId, dropMark.id, dropMark.position);
        }
        setDragId(null);
        setDropMark(null);
        lastMarkRef.current = '';
    };

    const onDragEnd = () => {
        flushDragOverRaf();
        setDragId(null);
        setDropMark(null);
        lastMarkRef.current = '';
    };

    const onListDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        // Only clear when leaving the whole list container, not just moving across children.
        if (!dragId) return;
        const rt = e.relatedTarget as Node | null;
        if (rt && (e.currentTarget as Node).contains(rt)) return;
        setDropMark(null);
        lastMarkRef.current = '';
    };

    return (
        <aside className={styles.Rail}>
            <div className={styles.SearchWrap}>
                <input
                    type="text"
                    value={searchTerm}
                    placeholder="Search Module"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search module"
                />
                {searchTerm ? (
                    <button type="button" className={styles.SearchClear} onClick={() => setSearchTerm('')} aria-label="Clear search">
                        <CloseIcon />
                    </button>
                ) : (
                    <SearchIcon />
                )}
            </div>

            <div className={styles.Divider} />

            <div className={styles.SectionHead}>
                <span className={styles.SectionTitle}>Select Modules</span>
                <div
                    className={styles.SelectAll}
                    role="button"
                    tabIndex={0}
                    onClick={() => (allEnabled ? deselectAll() : selectAll())}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            allEnabled ? deselectAll() : selectAll();
                        }
                    }}
                >
                    <span className={clsx(styles.CheckBox, { [styles.On]: allEnabled })}>{allEnabled ? <CheckTick /> : null}</span>
                    <span className={styles.SelectAllLabel}>Select All</span>
                </div>
            </div>

            <div className={styles.ModuleList} onDragLeave={onListDragLeave}>
                {loading ? <SkeletonCards count={10} /> : null}
                {!loading && items.length === 0 ? <div className={styles.Empty}>No modules match "{searchTerm}"</div> : null}
                {!loading &&
                    items.map((item) => {
                        const noData = !item.hasData && !item.locked;
                        const isDragging = dragId === item.id;
                        const showBefore = dropMark?.id === item.id && dropMark.position === 'before' && dragId !== item.id;
                        const showAfter = dropMark?.id === item.id && dropMark.position === 'after' && dragId !== item.id;
                        return (
                            <div key={item.id} className={styles.CardSlot} onDragOver={onDragOver(item.id)} onDrop={onDrop(item.id)}>
                                {showBefore ? <span className={styles.DropIndicator} aria-hidden /> : null}
                                <div
                                    className={clsx(styles.Card, {
                                        [styles.Dragging]: isDragging,
                                        [styles.Locked]: item.locked,
                                        [styles.NoData]: noData,
                                        // `On` is now the explicit toggled-on state — gives the card a
                                        // grey background to read as "selected". When the toggle is off
                                        // (and not locked / no-data), the card stays white.
                                        [styles.On]: item.enabled && !item.locked && !noData,
                                        [styles.Disabled]: !item.enabled && !item.locked,
                                    })}
                                    draggable={!item.locked}
                                    onDragStart={onDragStart(item.id, item.locked)}
                                    onDragEnd={onDragEnd}
                                    onMouseEnter={(e) => {
                                        if (item.locked || noData)
                                            openTooltip(item.locked ? UNLOCK_MODULE_TOOLTIP : DATA_NOT_AVAILABLE_LABEL, e.currentTarget);
                                    }}
                                    onMouseLeave={closeTooltip}
                                >
                                    <div className={styles.CardHeader}>
                                        <span
                                            className={styles.DragHandle}
                                            aria-label="Drag to reorder"
                                            title={item.locked ? 'Locked modules cannot be reordered' : 'Drag to reorder'}
                                        >
                                            <DragHandleIcon />
                                        </span>
                                        <Button
                                            type="button"
                                            className={clsx(styles.Body, styles.BodyButton)}
                                            text={
                                                <>
                                                    <span className={styles.Category}>{item.category}</span>
                                                    <span className={styles.Name}>{item.title}</span>
                                                </>
                                            }
                                            aria-label={`${GO_TO_SECTION_LABEL} ${item.title} in preview`}
                                            disabled={!item.enabled || item.locked || noData}
                                            onClick={() => onCardActivate?.(item.id)}
                                        />
                                        {item.locked ? (
                                            <span className={styles.LockIcon} aria-label="Locked">
                                                <LockIcon />
                                            </span>
                                        ) : noData ? (
                                            <span className={styles.NoDataIcon} aria-label="Data not available">
                                                <NoDataIcon />
                                            </span>
                                        ) : (
                                            <Button
                                                type="button"
                                                className={clsx(styles.Toggle, { [styles.On]: item.enabled })}
                                                text={<span className={styles.Knob} />}
                                                onClick={() => toggleModule(item.id)}
                                                aria-label={`Toggle ${item.title}`}
                                                aria-pressed={item.enabled}
                                                title={item.enabled ? 'Remove from report' : 'Add to report'}
                                            />
                                        )}
                                    </div>
                                    {item.enabled && item.subOptions ? (
                                        <SubOptionPanel
                                            options={item.subOptions}
                                            onToggle={(subId) => toggleSubOption(item.id, subId)}
                                            onActivate={(subId) => onSubOptionActivate?.(item.id, subId)}
                                            onLabelEnter={openTruncatedLabelTooltip}
                                            onLabelLeave={closeTooltip}
                                        />
                                    ) : null}
                                </div>
                                {showAfter ? <span className={styles.DropIndicator} aria-hidden /> : null}
                            </div>
                        );
                    })}
            </div>
            {tooltip ? (
                <span className={styles.Tooltip} style={{ top: tooltip.top, left: tooltip.left }}>
                    {tooltip.text}
                </span>
            ) : null}
        </aside>
    );
};

export default ModuleRail;
